import React, { useState } from 'react';
import { Upload, Download, Trash2 } from 'lucide-react';
import { FlightsTable } from '../components/flights/FlightsTable';
import { UploadFlightsModal } from '../components/flights/UploadFlightsModal';
import { generateFlightTemplate } from '../utils/flightValidation';
import { FlightSchema } from '../types/flight';
import { useFlights } from '../hooks/useFlights';
import { useFlightsStore } from '../stores/flightsStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

export const FlightsPage = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { translations } = useLanguage();  // Access translations from the context
  const { flights, loading, error } = useFlights();
  const { uploadFlights, deleteAll } = useFlightsStore();

  const handleDownloadTemplate = () => {
    const template = generateFlightTemplate();
    const ws = XLSX.utils.json_to_sheet([template]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "flights-template.xlsx");
  };

  const handleUpload = async (data: FlightSchema[]) => {
    try {
      const result = await uploadFlights(data);
      return { message: result.message };
    } catch (error: any) {
      throw new Error(error.message || translations?.flightsPage?.uploadError || 'An error occurred while uploading data');
    }
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAll();
      toast.success(translations?.flightsPage?.deleteSuccess || 'All flights have been deleted successfully');
    } catch (error: any) {
      toast.error(error.message || translations?.flightsPage?.deleteError || 'An error occurred while deleting flights');
    }
    setIsDeleteConfirmOpen(false);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {translations?.flightsPage?.header || 'Flight Schedules'}
        </h1>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadTemplate}
            className="bg-primary-50 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-100 
                     transition-colors flex items-center gap-2"
          >
            <Download size={20} />
            <span>{translations?.flightsPage?.downloadTemplate || 'Upload Template Example'}</span>
          </button>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 
                     transition-colors flex items-center gap-2"
          >
            <Upload size={20} />
            <span>{translations?.flightsPage?.addFlights || 'Add Flights'}</span>
          </button>
          <button
            onClick={() => setIsDeleteConfirmOpen(true)}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 
                     transition-colors flex items-center gap-2"
          >
            <Trash2 size={20} />
            <span>{translations?.flightsPage?.deleteAll || 'Delete All Flights'}</span>
          </button>
        </div>
      </div>

      <FlightsTable flights={flights} />
      
      <UploadFlightsModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteAll}
        title={translations?.flightsPage?.deleteAllTitle || 'Delete All Flights'}
        message={translations?.flightsPage?.deleteAllMessage || 'Are you sure you want to delete all flights? This action cannot be undone.'}
        type="danger"
        confirmText={translations?.flightsPage?.confirmDelete || 'Delete All'}
        cancelText={translations?.flightsPage?.cancel || 'Cancel'}
      />
    </div>
  );
};
